import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import DashboardCard from '../components/DashboardCard';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useSelector, useDispatch } from 'react-redux';
import CircularLoading from '../components/CircularLoading';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { FONT_FAMILY } from 'common/sharedFunctions';
import Money from '@mui/icons-material/Money';
import People from '@mui/icons-material/People';
import DriveEta from '@mui/icons-material/DriveEta';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Chart from '../components/ApexChart';

// Modern dashboard styles (Sarı Tema)
const modernColors = {
    primary: '#FFC107', // Ana Sarı (Amber 500)
    primaryLight: '#FFD54F', // Açık Sarı (Amber 300)
    primaryDark: '#FFA000', // Koyu Sarı (Amber 700)
    secondary: '#F9FAFB', // Açık gri
    success: '#10B981', // Yeşil (Başarı için)
    warning: '#FFA000', // Koyu sarı (Uyarı için ana sarıdan farklı olabilir)
    error: '#EF4444', // Kırmızı (Hata için)
    info: '#3B82F6', // Mavi (Bilgi için)
    textPrimary: '#1F2937',
    textSecondary: '#4B5563',
    background: '#FFFFFF',
    cardBg: '#FFFFFF',
    border: '#E5E7EB',
    hover: '#FFFBEB', // Açık sarı hover
    active: '#FFF8E1', // Amber 50
};

const DEFAULT_MAP_CENTER = { lat: 41.0082, lng: 28.9784 };

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector((state) => state.usersdata);
    const bookinglistdata = useSelector((state) => state.bookinglistdata);
    const settingsdata = useSelector((state) => state.settingsdata);
    const auth = useSelector((state) => state.auth);
    const cars = useSelector((state) => state.cartypes.cars);
    const dashboardUsers = usersdata.staticusers || usersdata.users;
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers, fetchUsersOnce } = api;

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        // fetchUsersOnce is dispatched here (instead of inheriting from AuthLoading) because the
        // admin/fleetadmin Dashboard is the only landing page that needs the full users tree.
        // Pulling it on Dashboard mount rather than on login means admins who go straight to
        // /bookings or /allreports never pay the ~5.5 MB /users download.
        dispatch(fetchUsersOnce());
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers, fetchUsersOnce]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                (position) =>
                    setMylocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    }),
                (error) => console.log(error)
            );
        }
    }, [mylocation]);

    const mapCenter =
        mylocation || (locations.length > 0 ? { lat: locations[0].lat, lng: locations[0].lng } : DEFAULT_MAP_CENTER);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter((bkg) => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];

            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].location) {
                    let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                    let bookingRef = null;
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) {
                            carImage = cars[j].image;
                        }
                    }
                    for (let j = 0; j < liveBookings.length; j++) {
                        if (liveBookings[j].driver === drivers[i].uid) {
                            bookingRef = liveBookings[j].reference;
                        }
                    }
                    locs.push({
                        id: i,
                        lat: drivers[i].location.lat,
                        lng: drivers[i].location.lng,
                        drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                        carnumber: drivers[i].vehicleNumber,
                        cartype: drivers[i].carType,
                        carImage: carImage,
                        bookingRef: bookingRef,
                        fleetadmin: drivers[i].fleetadmin,
                    });
                }
            }
            if (auth.profile.usertype === 'fleetadmin') {
                locs = locs.filter((driver) => driver.fleetadmin === auth.profile.uid);
            }
            setLocations(locs);
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);
    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if (
                    (bookinglistdata.bookings[i].status === 'PAID' ||
                        bookinglistdata.bookings[i].status === 'COMPLETE') &&
                    ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid &&
                        auth.profile.usertype === 'fleetadmin') ||
                        auth.profile.usertype === 'admin')
                ) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (
                            tDate.getDate() === today.getDate() &&
                            tDate.getMonth() === today.getMonth() &&
                            tDate.getFullYear() === today.getFullYear()
                        ) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin') {
                        if (
                            tDate.getDate() === today.getDate() &&
                            tDate.getMonth() === today.getMonth() &&
                            tDate.getFullYear() === today.getFullYear()
                        ) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal || 2));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal || 2));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal || 2));
        }
    }, [bookinglistdata.bookings, settings, auth.profile.uid, auth.profile.usertype]);

    const months = [
        t('january'),
        t('february'),
        t('march'),
        t('april'),
        t('may'),
        t('june'),
        t('july'),
        t('august'),
        t('september'),
        t('october'),
        t('november'),
        t('december'),
    ];

    const barChartOptions = {
        chart: {
            height: 350,
            type: 'area',
            fontFamily: FONT_FAMILY,
            toolbar: {
                show: false,
            },
            background: 'transparent',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150,
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350,
                },
            },
        },
        colors: [modernColors.primary, modernColors.error],
        dataLabels: {
            enabled: false,
        },
        stroke: {
            curve: 'smooth',
        },
        xaxis: {
            categories: [
                t('jan'),
                t('feb'),
                t('mar'),
                t('apr'),
                t('may'),
                t('jun'),
                t('jul'),
                t('aug'),
                t('sep'),
                t('oct'),
                t('nov'),
                t('dec'),
            ],
            labels: {
                style: {
                    fontFamily: FONT_FAMILY,
                    colors: modernColors.textSecondary,
                    fontSize: '12px',
                    fontWeight: 500,
                },
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
        yaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY,
                    colors: modernColors.textSecondary,
                    fontSize: '12px',
                    fontWeight: 500,
                },
                formatter: function (value) {
                    return value.toFixed(0);
                },
            },
        },
        title: {
            style: {
                fontFamily: FONT_FAMILY,
                color: modernColors.textPrimary,
            },
        },
        subtitle: {
            style: {
                fontFamily: FONT_FAMILY,
                color: modernColors.textSecondary,
            },
        },
        tooltip: {
            theme: 'light',
            x: {
                show: true,
            },
            style: {
                fontFamily: FONT_FAMILY,
                fontSize: '12px',
            },
            marker: {
                show: true,
            },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontFamily: FONT_FAMILY,
            fontSize: '13px',
            fontWeight: 500,
            itemMargin: {
                horizontal: 10,
                vertical: 0,
            },
            labels: {
                colors: modernColors.textSecondary,
            },
        },
        grid: {
            borderColor: modernColors.border,
            strokeDashArray: 4,
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },
            xaxis: {
                lines: {
                    show: false,
                },
            },
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.2,
                stops: [0, 100],
            },
        },
        states: {
            hover: {
                filter: {
                    type: 'lighten',
                    value: 0.05,
                },
            },
            active: {
                filter: {
                    type: 'none',
                },
            },
        },
        plotOptions: {
            bar: {
                columnWidth: '60%',
                borderRadius: 4,
            },
        },
    };

    const barChart = [
        {
            name: t('completed_bookings'),
            data: allCompleteCount,
            color: modernColors.primary,
        },
        {
            name: t('cancelled_bookings'),
            data: allCancelCount,
            color: modernColors.error,
        },
    ];
    // NOTE (S4 2026-05-26): bookinglistdata.bookings is now capped at the most-recent 500 records
    // for the admin role (see ADMIN_BOOKINGS_FETCH_LIMIT in common/src/config/configureFirebase.js).
    // dailygross / monthlygross totals derived from this slice are accurate only over that window;
    // true lifetime totals should aggregate server-side. Tracked as a follow-up to S4.
    useEffect(() => {
        if (dashboardUsers) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < dashboardUsers.length; i++) {
                if (dashboardUsers[i].usertype === 'driver') {
                    if (
                        (auth.profile.usertype === 'fleetadmin' &&
                            dashboardUsers[i].fleetadmin === auth.profile.uid) ||
                        auth.profile.usertype === 'admin'
                    ) {
                        driverCount = driverCount + 1;
                    }
                }
                if (dashboardUsers[i].driverActiveStatus === true) {
                    if (
                        (auth.profile.usertype === 'fleetadmin' &&
                            dashboardUsers[i].fleetadmin === auth.profile.uid) ||
                        auth.profile.usertype === 'admin'
                    ) {
                        activeCount = activeCount + 1;
                    }
                }
                if (dashboardUsers[i].usertype === 'customer') {
                    if (
                        (auth.profile.usertype === 'fleetadmin' &&
                            dashboardUsers[i].fleetadmin === auth.profile.uid) ||
                        auth.profile.usertype === 'admin'
                    ) {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
            setCustomerCount(0);
        }
    }, [dashboardUsers, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (
                        months[i] === months[tDate.getMonth()] &&
                        (bookinglistdata.bookings[j].status === 'PAID' ||
                            bookinglistdata.bookings[j].status === 'COMPLETE')
                    ) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    const cardStyle = {
        backgroundColor: modernColors.cardBg,
        borderRadius: '8px',
        padding: '20px',
        height: '100%',
        border: `1px solid ${modernColors.border}`,
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        },
    };

    return usersdata.loading ? (
        <CircularLoading />
    ) : (
        <Box sx={{ flexGrow: 1, p: 2, backgroundColor: modernColors.secondary /* Açık gri arka plan */ }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography
                        variant="h5"
                        style={{
                            fontFamily: FONT_FAMILY,
                            marginBottom: 20,
                            fontWeight: 700,
                            color: modernColors.textPrimary,
                        }}
                    >
                        {t('dashboard_text')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardCard
                        title={'Bugün'}
                        value={`${settings.symbol || ''}${dailygross}`}
                        icon={<Money sx={{ fontSize: 32, color: modernColors.primaryDark }} />}
                        color={modernColors.primary} // Kart arka planı için ana sarı
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardCard
                        title={'Aylık Gelir'}
                        value={`${settings.symbol || ''}${monthlygross}`}
                        icon={<Money sx={{ fontSize: 32, color: modernColors.success }} />}
                        color={modernColors.success} // Yeşil
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardCard
                        title={'Toplam Gelir'}
                        value={`${settings.symbol || ''}${totalgross}`}
                        icon={<Money sx={{ fontSize: 32, color: modernColors.warning }} />}
                        color={modernColors.warning} // Koyu Sarı
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardCard
                        title={'Müşteri Sayısı'}
                        value={customerCount}
                        icon={<People sx={{ fontSize: 32, color: modernColors.info }} />}
                        color={modernColors.info} // Mavi
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardCard
                        title={'Toplam Sürücü'}
                        value={driverCount}
                        icon={<DriveEta sx={{ fontSize: 32, color: modernColors.primaryDark }} />}
                        color={modernColors.primary} // Sarı
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                    <DashboardCard
                        title={'Aktif Sürücü'}
                        value={activeCount}
                        icon={<CheckCircle sx={{ fontSize: 32, color: modernColors.success }} />}
                        color={modernColors.success} // Yeşil
                    />
                </Grid>

                {/* Sürücüler Gerçek Zamanlı Harita */}
                <Grid item xs={12} md={12} lg={12}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 3,
                            height: '100%',
                            borderRadius: '12px', // Daha yuvarlak köşeler
                            border: `1px solid ${modernColors.border}`,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.06)', // Hafif gölge
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
                            },
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                fontFamily: FONT_FAMILY,
                                fontWeight: 600,
                                color: modernColors.textPrimary,
                            }}
                        >
                            {t('real_time_driver_section_text')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                height: '450px',
                                width: '100%',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            <GoogleMap
                                    zoom={10}
                                    center={mapCenter}
                                    mapContainerStyle={{
                                        height: '100%',
                                        width: '100%',
                                    }}
                                    options={{
                                        fullscreenControl: false,
                                        mapTypeControl: false,
                                        streetViewControl: false,
                                        zoomControl: true,
                                        styles: [
                                            {
                                                featureType: 'water',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#e9e9e9' }, { lightness: 17 }],
                                            },
                                            {
                                                featureType: 'landscape',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#f5f5f5' }, { lightness: 20 }],
                                            },
                                            {
                                                featureType: 'road.highway',
                                                elementType: 'geometry.fill',
                                                stylers: [{ color: '#ffffff' }, { lightness: 17 }],
                                            },
                                            {
                                                featureType: 'road.highway',
                                                elementType: 'geometry.stroke',
                                                stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }],
                                            },
                                            {
                                                featureType: 'road.arterial',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#ffffff' }, { lightness: 18 }],
                                            },
                                            {
                                                featureType: 'road.local',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#ffffff' }, { lightness: 16 }],
                                            },
                                            {
                                                featureType: 'poi',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#f5f5f5' }, { lightness: 21 }],
                                            },
                                            {
                                                featureType: 'poi.park',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#dedede' }, { lightness: 21 }],
                                            },
                                            {
                                                elementType: 'labels.text.stroke',
                                                stylers: [
                                                    { visibility: 'on' },
                                                    { color: '#ffffff' },
                                                    { lightness: 16 },
                                                ],
                                            },
                                            {
                                                elementType: 'labels.text.fill',
                                                stylers: [{ saturation: 36 }, { color: '#333333' }, { lightness: 40 }],
                                            },
                                            {
                                                elementType: 'labels.icon',
                                                stylers: [{ visibility: 'off' }],
                                            },
                                            {
                                                featureType: 'transit',
                                                elementType: 'geometry',
                                                stylers: [{ color: '#f2f2f2' }, { lightness: 19 }],
                                            },
                                            {
                                                featureType: 'administrative',
                                                elementType: 'geometry.fill',
                                                stylers: [{ color: '#fefefe' }, { lightness: 20 }],
                                            },
                                            {
                                                featureType: 'administrative',
                                                elementType: 'geometry.stroke',
                                                stylers: [{ color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 }],
                                            },
                                        ],
                                    }}
                                >
                                    {locations.map((marker) => (
                                        <Marker
                                            key={marker.id}
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            icon={{
                                                url: marker.carImage,
                                                scaledSize: new window.google.maps.Size(32, 32),
                                            }}
                                            onClick={() => setSelectedMarker(marker.id)}
                                        >
                                            {selectedMarker === marker.id && (
                                                <InfoWindow
                                                    position={{ lat: marker.lat, lng: marker.lng }}
                                                    onCloseClick={() => setSelectedMarker(null)}
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: '8px',
                                                            bgcolor: '#fff',
                                                            width: '160px',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontFamily: FONT_FAMILY,
                                                                color: modernColors.textPrimary,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {marker.drivername}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block',
                                                                fontFamily: FONT_FAMILY,
                                                                color: modernColors.textSecondary,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {marker.carnumber}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block',
                                                                fontFamily: FONT_FAMILY,
                                                                color: modernColors.textSecondary,
                                                            }}
                                                        >
                                                            {marker.cartype}
                                                        </Typography>
                                                        {marker.bookingRef ? (
                                                            <Box
                                                                sx={{
                                                                    mt: 1,
                                                                    pt: 1,
                                                                    borderTop: `1px solid ${modernColors.border}`,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        bgcolor: modernColors.success,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontFamily: FONT_FAMILY,
                                                                        color: modernColors.primary,
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    {t('booking')}: {marker.bookingRef}
                                                                </Typography>
                                                            </Box>
                                                        ) : null}
                                                    </Box>
                                                </InfoWindow>
                                            )}
                                        </Marker>
                                    ))}
                            </GoogleMap>
                        </Box>
                    </Card>
                </Grid>

                {/* Rezervasyon Grafiği */}
                <Grid item xs={12} md={12} lg={12}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 3,
                            height: '100%',
                            borderRadius: '12px',
                            border: `1px solid ${modernColors.border}`,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: modernColors.textPrimary }}
                            >
                                {t('booking_chart')}
                            </Typography>
                            {/* Legend (renkler güncellendi) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: modernColors.primary /* Sarı */,
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ fontFamily: FONT_FAMILY, color: modernColors.textSecondary }}
                                    >
                                        {t('completed_bookings')}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: modernColors.error /* Kırmızı */,
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ fontFamily: FONT_FAMILY, color: modernColors.textSecondary }}
                                    >
                                        {t('cancelled_bookings')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ height: 400 }}>
                            <Chart options={barChartOptions} series={barChart} type="area" height="100%" width="100%" />
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
